import { useEffect, useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import {
  loadOdooInvoices,
  loadInvoicePdf,
  downloadBase64Pdf,
  paymentLabel,
  type OdooInvoice,
} from '../../lib/odooInvoices';

/**
 * Onglet « Facturation » du back-office CLIENT : liste les factures du resto depuis
 * Odoo (Edge Function `odoo-invoices`, sécurisée par appartenance) et permet de les
 * télécharger en PDF. Si Odoo n'est pas configuré ou le resto non relié, message clair.
 */
export default function ClientBillingSection({ tenant }: { tenant: Tenant }) {
  const [invoices, setInvoices] = useState<OdooInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadOdooInvoices(tenant.slug).then((res) => {
      if (cancelled) return;
      if (res.ok) setInvoices(res.invoices);
      else setError(res.error ?? 'Chargement impossible.');
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [tenant.slug]);

  const download = async (inv: OdooInvoice) => {
    setDownloading(inv.id);
    const res = await loadInvoicePdf(tenant.slug, inv.id);
    setDownloading(null);
    if (!res.ok || !res.pdfBase64) {
      alert(res.error ?? 'Téléchargement impossible.');
      return;
    }
    downloadBase64Pdf(res.pdfBase64, res.filename ?? `${inv.name}.pdf`);
  };

  const money = (n: number, cur: string | null) =>
    `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${cur ? ' ' + cur : ' €'}`;

  return (
    <div className="max-w-3xl">
      <div className="mb-4">
        <h1 className="text-xl font-bold">🧾 Facturation</h1>
        <p className="mt-1 text-sm text-slate-500">
          Vos factures Lil &amp; Co, synchronisées depuis notre comptabilité (Odoo). Téléchargez le PDF
          de chaque facture.
        </p>
      </div>

      {loading ? (
        <p className="opacity-70">Chargement de vos factures…</p>
      ) : error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="font-semibold">Factures indisponibles pour le moment.</div>
          <div className="mt-1 opacity-80">{error}</div>
          <div className="mt-2 text-xs opacity-70">
            Si le problème persiste, contactez Lil &amp; Co — il se peut que votre compte ne soit pas
            encore relié à la facturation.
          </div>
        </div>
      ) : invoices.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
          Aucune facture pour le moment.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase opacity-60">
              <tr>
                <th className="px-3 py-2">Facture</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2 text-right">Montant</th>
                <th className="px-3 py-2 text-center">Statut</th>
                <th className="px-3 py-2 text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium">{inv.name || `#${inv.id}`}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {inv.date ? new Date(inv.date).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{money(inv.total, inv.currency)}</td>
                  <td className="px-3 py-2 text-center">
                    <span
                      className={
                        'rounded-full px-2 py-0.5 text-xs font-semibold ' +
                        (inv.paymentState === 'paid'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-800')
                      }
                    >
                      {paymentLabel(inv.paymentState)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => download(inv)}
                      disabled={downloading === inv.id || inv.state === 'draft'}
                      className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 disabled:opacity-40"
                      title={inv.state === 'draft' ? 'Facture en brouillon' : 'Télécharger le PDF'}
                    >
                      {downloading === inv.id ? '…' : '⬇️ PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
